import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Close } from 'assets/icons';
import Button from 'components/button';
import {
  INVOICE_REMINDER_CHANGED,
  REMINDER_LEVELS,
  REMINDER_MESSAGE_MAX_LENGTH,
  countText,
  formatFullDay,
  formatReminderEuros,
  invoiceRemindersText,
} from 'components/invoiceReminders/invoiceRemindersText';
import {
  contactName,
  contactQualifier,
  createRequestId,
} from 'components/invoiceSendMail/invoiceSendMailText';
import ModalBody from 'components/modal/components/body';
import ModalFooter from 'components/modal/components/footer';
import useModal from 'components/modal/useModal';
import { companyRoleLabel } from 'constants/companyRoles';
import UseContext from 'hooks/useContext';
import ApiDatabase from 'server';

const CHECKBOX_CLASS_NAME = 'w-4 h-4 accent-yellow-500 flex-shrink-0 mt-0.5';
const DRAFT_ABORT_KEY = 'admin/invoices/:number/reminders/draft';

const DRAFT_REFUSALS = [
  'INVOICE_NOT_REMINDABLE',
  'INVOICE_IN_LITIGATION',
  'INVOICE_NOT_FOUND',
];
const SEND_REFUSALS = [
  'INVOICE_SETTLED',
  'INVOICE_IN_LITIGATION',
  'INVOICE_NOT_REMINDABLE',
  'SEND_IN_PROGRESS',
  'LEVEL_INVALID',
  'MESSAGE_INVALID',
  'RECIPIENTS_INVALID',
  'REQUEST_ID_INVALID',
  'INVOICE_NOT_FOUND',
  'MAIL_SEND_FAILED',
  'PENNYLANE_READ_FAILED',
  'BILLING_MAIL_DISABLED',
  'BILLING_MAIL_TEMPLATE_MISSING',
];
// Refusals saying the invoice is no longer reminded: the api may have just
// written its status, so the tab and the detail behind read it again.
const STATE_MOVED_REFUSALS = [
  'INVOICE_SETTLED',
  'INVOICE_IN_LITIGATION',
  'INVOICE_NOT_REMINDABLE',
];

const refKey = (contact) => `${contact.ref.kind}:${contact.ref.id}`;

// The api recorded this request id as `failed` and answers the same 502 to
// every later attempt carrying it: a known failure, retried under a new id.
const KNOWN_SEND_FAILURE = 'MAIL_SEND_FAILED';

// No answer, a server failure, or the api still running this very request id:
// the first attempt may have gone (or will go) through with the body it carried.
const isOutcomeUnknown = (error) => {
  const code = error?.response?.data?.errorCode;
  if (code === KNOWN_SEND_FAILURE) return false;
  return (
    !error?.response ||
    error.response.status >= 500 ||
    code === 'SEND_IN_PROGRESS'
  );
};

/**
 * « Envoyer une relance » (JBWEB-1283, mockup 7.3). `modalData` is
 * `{ number }`. The api drafts the reminder (`GET …/reminders/draft`): the
 * step proposed, its default message, the company's billing contacts, those
 * who chose « Relances » ticked by default, and the next automatic reminder.
 * Another template is drafted again by the api; its message replaces the one
 * shown only while the admin has not changed it.
 *
 * As in « Envoyer par mail », the request id is drawn at the opening and kept
 * until the modal closes, and once an attempt's outcome is unknown the body
 * it carried is frozen: the api replays a known id without reading the new
 * body, so only that exact body may leave again. MAIL_SEND_FAILED is the
 * exception: that id is over and failed, so the body is let go and the next
 * attempt draws a new id (the api never sends a failure again by itself).
 */
const ModalInvoiceReminder = () => {
  const { toggle } = useModal();
  const { language, modalData, setModalDataReturn } = UseContext();
  const remindersText = invoiceRemindersText(language);
  const text = (path, values) => remindersText(`reminderModal.${path}`, values);
  const textRef = useRef(text);
  textRef.current = text;

  // Frozen at the opening: the screen behind may change meanwhile.
  const [number] = useState(() => {
    const value = Number(modalData?.number);
    return Number.isInteger(value) && value > 0 ? value : null;
  });
  const [requestId, setRequestId] = useState(createRequestId);
  const [draft, setDraft] = useState(null);
  const [draftFailure, setDraftFailure] = useState(null);
  const [loadKey, setLoadKey] = useState(0);
  const [level, setLevel] = useState(null);
  const [isLevelLoading, setIsLevelLoading] = useState(false);
  const [levelFailure, setLevelFailure] = useState(null);
  const [message, setMessage] = useState('');
  // The admin's ticks by contact; any other contact keeps the api's `preselected`.
  const [ticks, setTicks] = useState({});
  const [suspendAutomatic, setSuspendAutomatic] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendFailure, setSendFailure] = useState(null);
  // The body of an attempt whose outcome is unknown. A later refusal does not
  // prove the first is over, so only MAIL_SEND_FAILED on that same id (proof
  // that it failed) clears it while the modal is open.
  const [frozenBody, setFrozenBody] = useState(null);
  const isFrozen = frozenBody !== null;

  // Read by the answers of the drafts, which may land after newer edits.
  const messageRef = useRef('');
  messageRef.current = message;
  // The default message of the template loaded last: the message is the
  // admin's own as soon as it differs from it.
  const defaultMessageRef = useRef(null);
  const loadedLevelRef = useRef(null);
  const latestRead = useRef(0);
  const sendingRef = useRef(false);

  const readDraft = useCallback(
    (askedLevel, onAnswer, onFailure) => {
      latestRead.current += 1;
      const readId = latestRead.current;
      ApiDatabase.getInvoiceReminderDraft(
        { number, ...(askedLevel && { level: askedLevel }) },
        (data) => {
          if (readId === latestRead.current) onAnswer(data ?? {});
        },
        (error) => {
          if (readId !== latestRead.current || error?.name === 'CanceledError')
            return;
          // Never the error object in the console: axios keeps the request, token included.
          onFailure(error);
        },
        { abortKey: DRAFT_ABORT_KEY },
      );
    },
    [number],
  );

  useEffect(() => {
    if (number === null) return undefined;
    setDraft(null);
    setDraftFailure(null);
    readDraft(
      null,
      (data) => {
        setDraft(data);
        setLevel(data.level ?? null);
        loadedLevelRef.current = data.level ?? null;
        defaultMessageRef.current = data.message ?? '';
        setMessage(data.message ?? '');
      },
      (error) => {
        const code = error?.response?.data?.errorCode;
        const isRefused = DRAFT_REFUSALS.includes(code);
        setDraftFailure({
          text: textRef.current(`draftErrors.${isRefused ? code : 'generic'}`),
          canRetry: !isRefused,
        });
      },
    );
    return () => {
      latestRead.current += 1;
      ApiDatabase.abortRequest?.(DRAFT_ABORT_KEY);
    };
  }, [number, readDraft, loadKey]);

  if (number === null) return null;

  const isChoiceLocked = isSending || isFrozen;
  const contacts = draft?.contacts ?? [];
  const isTicked = (contact) => {
    const key = refKey(contact);
    return key in ticks ? ticks[key] : contact.preselected === true;
  };
  const tickedContacts = contacts.filter(isTicked);
  const messageLength = message.trim().length;
  const isMessageTooLong = messageLength > REMINDER_MESSAGE_MAX_LENGTH;
  const canSend = isFrozen
    ? !isSending
    : Boolean(draft) &&
      !isLevelLoading &&
      !isSending &&
      tickedContacts.length > 0 &&
      messageLength > 0 &&
      !isMessageTooLong;

  const chooseLevel = (nextLevel) => {
    if (isChoiceLocked || nextLevel === level) return;
    setLevel(nextLevel);
    setLevelFailure(null);
    setSendFailure(null);
    setIsLevelLoading(true);
    readDraft(
      nextLevel,
      (data) => {
        setIsLevelLoading(false);
        loadedLevelRef.current = nextLevel;
        const nextMessage = data.message ?? '';
        if (messageRef.current === defaultMessageRef.current) {
          setMessage(nextMessage);
        }
        defaultMessageRef.current = nextMessage;
        setDraft((current) => ({ ...current, ...data, level: nextLevel }));
      },
      () => {
        setIsLevelLoading(false);
        // The message shown still belongs to the template loaded before.
        setLevel(loadedLevelRef.current);
        setLevelFailure(textRef.current('levelFailed'));
      },
    );
  };

  const toggleContact = (contact) => {
    if (isChoiceLocked) return;
    setSendFailure(null);
    const key = refKey(contact);
    const ticked = isTicked(contact);
    setTicks((current) => ({ ...current, [key]: !ticked }));
  };

  const editMessage = (event) => {
    if (isChoiceLocked) return;
    setSendFailure(null);
    setMessage(event.target.value);
  };

  const buildBody = () => ({
    number,
    requestId,
    level,
    message,
    // References only: the api finds the addresses itself.
    recipients: tickedContacts.map(({ ref }) => ({
      kind: ref.kind,
      id: ref.id,
    })),
    suspendAutomatic: draft?.suspended ? false : suspendAutomatic,
  });

  const send = () => {
    if (!canSend || sendingRef.current) return;
    sendingRef.current = true;
    const body = frozenBody ?? buildBody();
    setIsSending(true);
    setSendFailure(null);
    ApiDatabase.postInvoiceManualReminder(
      body,
      (data) => {
        sendingRef.current = false;
        let successKey = 'sent';
        if (data?.replayed === true) successKey = 'replayed';
        else if (body.suspendAutomatic && data?.suspended) {
          successKey = 'sentSuspended';
        }
        toast.success(textRef.current(successKey, { number }));
        setModalDataReturn({ type: INVOICE_REMINDER_CHANGED, number });
        toggle();
      },
      (error) => {
        sendingRef.current = false;
        setIsSending(false);
        const code = error?.response?.data?.errorCode;
        if (code === KNOWN_SEND_FAILURE) {
          setFrozenBody(null);
          setRequestId(createRequestId());
        } else if (isOutcomeUnknown(error)) {
          setFrozenBody(body);
        }
        let failureKey = 'errors.generic';
        if (!error?.response) failureKey = 'errors.network';
        else if (SEND_REFUSALS.includes(code)) failureKey = `errors.${code}`;
        setSendFailure(textRef.current(failureKey));
        if (STATE_MOVED_REFUSALS.includes(code)) {
          setModalDataReturn({ type: INVOICE_REMINDER_CHANGED, number });
        }
      },
    );
  };

  const invoice = draft?.invoice ?? {};
  const owed = invoice.remainingAmount ?? invoice.amountInclTax;
  const dueDay = formatFullDay(invoice.dueDate);
  const summaryParts = [
    text('summary.number', { number: invoice.number ?? number }),
    invoice.companyName,
    owed == null
      ? null
      : text('summary.remaining', { amount: formatReminderEuros(owed) }),
    invoice.lateDays > 0
      ? countText(text, 'summary.late', invoice.lateDays, {
          days: invoice.lateDays,
        })
      : dueDay && text('summary.dueOn', { date: dueDay }),
  ].filter(Boolean);
  const levelOptions = (draft?.levels ?? [])
    .map((step) => step?.level)
    .filter((step) => REMINDER_LEVELS.includes(step));
  const nextAutomaticDay = formatFullDay(draft?.nextAutomaticAt);

  const renderContact = (contact) => {
    const roleLabel =
      contact.label === 'member' && contact.role != null
        ? companyRoleLabel(language, contact.role)
        : '';
    return (
      <label
        key={refKey(contact)}
        className="flex items-start gap-3 px-4 py-3 cursor-pointer"
      >
        <input
          type="checkbox"
          className={CHECKBOX_CLASS_NAME}
          checked={isTicked(contact)}
          disabled={isChoiceLocked}
          onChange={() => toggleContact(contact)}
        />
        <span className="flex flex-col">
          <span className="font-medium text-gray-900">
            {contactName(text, contact)}
          </span>
          <span className="text-gray-500">
            {[contact.email, contactQualifier(text, contact, roleLabel)]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </span>
      </label>
    );
  };

  let body;
  if (draftFailure) {
    body = (
      <div
        role="alert"
        className="flex flex-col items-start gap-3 rounded-lg bg-red-50 px-4 py-3 text-red-700"
      >
        <span>{draftFailure.text}</span>
        {draftFailure.canRetry && (
          <Button
            color="SECONDARY"
            onClick={() => setLoadKey((key) => key + 1)}
          >
            {text('reload')}
          </Button>
        )}
      </div>
    );
  } else if (!draft) {
    body = (
      <div role="status" className="py-6 text-center text-gray-500">
        {text('loading')}
      </div>
    );
  } else {
    body = (
      <div className="flex flex-col gap-5">
        <div className="text-gray-500">{summaryParts.join(' · ')}</div>
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-2 font-medium text-gray-700">
            {text('recipientsLabel')}
          </legend>
          {contacts.length > 0 ? (
            <div className="flex flex-col divide-y divide-gray-100 rounded-lg border border-gray-100">
              {contacts.map(renderContact)}
            </div>
          ) : (
            <div className="rounded-lg border border-gray-100 px-4 py-3 text-gray-500">
              {text('recipients.none')}
            </div>
          )}
        </fieldset>
        <div className="flex flex-col gap-2">
          <label
            htmlFor="invoice-reminder-level"
            className="font-medium text-gray-700"
          >
            {text('levelLabel')}
          </label>
          <select
            id="invoice-reminder-level"
            className="h-10 rounded-md border border-gray-300 px-3 text-gray-900 disabled:bg-gray-50"
            value={level ?? ''}
            disabled={isChoiceLocked}
            onChange={(event) => chooseLevel(event.target.value)}
          >
            {levelOptions.map((option) => (
              <option key={option} value={option}>
                {text(`levels.${option}`)}
              </option>
            ))}
          </select>
          {isLevelLoading && (
            <span role="status" className="text-xs text-gray-500">
              {text('levelLoading')}
            </span>
          )}
          {levelFailure && (
            <span role="alert" className="text-xs text-red-700">
              {levelFailure}
            </span>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <label
            htmlFor="invoice-reminder-message"
            className="font-medium text-gray-700"
          >
            {text('messageLabel')}
          </label>
          <textarea
            id="invoice-reminder-message"
            rows={7}
            className="rounded-md border border-gray-300 px-3 py-2 text-gray-900 disabled:bg-gray-50"
            value={message}
            readOnly={isChoiceLocked}
            onChange={editMessage}
          />
          {isMessageTooLong && (
            <span role="alert" className="text-xs text-red-700">
              {text('messageTooLong', {
                max: REMINDER_MESSAGE_MAX_LENGTH,
                count: messageLength,
              })}
            </span>
          )}
          <span className="text-xs text-gray-500">{text('attachment')}</span>
        </div>
        <label className="flex items-start gap-3 border-b border-gray-100 pb-4 cursor-pointer">
          <input
            type="checkbox"
            className={CHECKBOX_CLASS_NAME}
            checked={draft.suspended ? true : suspendAutomatic}
            disabled={isChoiceLocked || draft.suspended}
            onChange={() => {
              if (isChoiceLocked) return;
              setSuspendAutomatic((current) => !current);
            }}
          />
          <span className="flex flex-col">
            <span className="font-medium text-gray-900">{text('suspend')}</span>
            {draft.suspended ? (
              <span className="text-gray-500">{text('alreadySuspended')}</span>
            ) : (
              nextAutomaticDay && (
                <span className="text-gray-500">
                  {text('nextAutomatic', { date: nextAutomaticDay })}
                </span>
              )
            )}
          </span>
        </label>
        {(sendFailure || isFrozen) && (
          <div
            role="alert"
            className="flex flex-col gap-2 rounded-lg bg-red-50 px-4 py-3 text-red-700"
          >
            {sendFailure && <p>{sendFailure}</p>}
            {isFrozen && <p>{text('frozenNotice')}</p>}
          </div>
        )}
      </div>
    );
  }

  let sendLabel = text('send');
  if (isSending) sendLabel = text('sending');
  else if (isFrozen) sendLabel = text('retrySend');

  return (
    <>
      <div className="flex items-start justify-between gap-3 px-6 pt-5 pb-3 rounded-t">
        <h3 className="ft-lg tab:text-xl text-[#202124] font-semibold my-auto">
          {text('title')}
        </h3>
        <Button
          size="TABLE"
          color="TABLE"
          aria-label={text('close')}
          title={text('close')}
          disabled={isSending}
          className="disabled:opacity-40 disabled:cursor-not-allowed"
          onClick={toggle}
        >
          <Close wh={20} color="#374151" />
        </Button>
      </div>
      <ModalBody>{body}</ModalBody>
      <ModalFooter>
        <Button
          size="LG"
          color={isSending ? 'DISABLED' : 'SECONDARY'}
          disabled={isSending}
          onClick={toggle}
        >
          {text('cancel')}
        </Button>
        {!draftFailure && (
          <Button
            size="LG"
            color={canSend ? 'PRIMARY' : 'DISABLED'}
            disabled={!canSend}
            onClick={send}
          >
            {sendLabel}
          </Button>
        )}
      </ModalFooter>
    </>
  );
};

export default ModalInvoiceReminder;
